<template>
  <div class="season-selector">
    <!-- Empty state: no seasons -->
    <div v-if="seasons.length === 0" class="no-seasons">
      <AeButton variant="primary" @click="createSeason">
        <AeIcon name="plus" :size="16" />
        {{ t('seasonSelector.createSeason') }}
      </AeButton>
    </div>

    <!-- Select with seasons -->
    <div v-else class="season-select-wrapper">
      <AeSelect
        :model-value="modelValue"
        @update:model-value="$emit('update:modelValue', $event)"
        :options="seasonOptions"
        :placeholder="t('seasonSelector.selectSeason')"
        class="season-ae-select"
      />
      <AeButton 
        v-if="showCreateButton"
        variant="ghost" 
        size="small" 
        @click="createSeason"
        class="inline-create-btn"
      >
        <AeIcon name="plus" :size="16" />
      </AeButton>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AeButton, AeIcon, AeSelect } from '@aevue/components'

const { t } = useI18n()

const props = defineProps({
  modelValue: {
    type: String,
    default: null
  },
  seasons: {
    type: Array,
    required: true
  },
  showLabel: {
    type: Boolean,
    default: true
  },
  showCreateButton: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'create-season'])

// Sort seasons by year descending (most recent first)
const sortedSeasons = computed(() => {


  let s=[...props.seasons].sort((a, b) => {
    const date_a = a.date_from ? new Date(a.date_from) : null
    const date_b = b.date_from ? new Date(b.date_from) : null

    if (!date_a && !date_b) return 0
    if (!date_a) return 1
    if (!date_b) return -1
    return date_b - date_a
  })

  return s
})

// Options for AeSelect
const seasonOptions = computed(() => {
  const options = sortedSeasons.value.map(season => ({
    value: season.id_season,
    label: season.name || season.season_name
  }))
  
  // Add create option
  options.push({
    value: '_create_',
    label: t('seasonSelector.createNewSeason')
  })
  
  return options
})

// Auto-select latest season if not already selected
watch(() => props.seasons, (newSeasons) => {
  if (newSeasons.length > 0 && !props.modelValue) {
    const latestSeason = sortedSeasons.value[0]
    emit('update:modelValue', latestSeason.id_season)
  }
}, { immediate: true })

// Handle select change including "create new" option
watch(() => props.modelValue, (newValue) => {
  if (newValue === '_create_') {
    emit('create-season')
    // Reset to previous value after emitting
    if (sortedSeasons.value.length > 0) {
      emit('update:modelValue', sortedSeasons.value[0].id_season)
    }
  }
})

function createSeason() {
  emit('create-season')
}
</script>

<style scoped>
.season-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.no-seasons {
  display: flex;
  align-items: center;
}

.season-select-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.season-ae-select {
  width: 6.2em;
}

.season-select-wrapper label {
  font-weight: 500;
  font-size: 0.875rem;
  white-space: nowrap;
}

.season-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  min-width: 200px;
}

.season-select:focus {
  outline: none;
  border-color: var(--primary-color, #4299e1);
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.create-option {
  font-weight: 600;
  color: var(--primary-color, #4299e1);
  border-top: 1px solid var(--border-color, #e2e8f0);
  margin-top: 0.25rem;
  padding-top: 0.25rem;
}

.inline-create-btn {
  padding: 0.5rem;
}

@media (max-width: 768px) {
  .season-select-label {
    display: none;
  }
}
</style>
